<template>
	<v-dialog
		v-model="open"
		max-width="700"
		@click:outside="$emit('close')"
		:fullscreen="$vuetify.breakpoint.xs ? true : false"
		:transition="$vuetify.breakpoint.xs ? 'scroll-y-reverse-transition' : ''"
	>
		<v-card>
			<v-toolbar dark color="primary" dense>
				<v-toolbar-title style="color: white">日報詳細</v-toolbar-title>
				<v-spacer></v-spacer>
				<v-btn icon dark @click="$emit('close')" color="white">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-sheet elevation="0" class="pa-2">
				<v-row v-if="$store.getters['auth/isOwner'](report)" no-gutters justify="end">
					<v-tooltip
						bottom
						v-if="
							report.daily_report_template_id && report.daily_report_template.status == 'archived'
						"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-btn class="mr-2" v-bind="attrs" v-on="on" color="grey"
								><v-icon class="mr-2">mdi-pencil</v-icon>編集する</v-btn
							>
						</template>
						<span>元となる日報テンプレートがアーカイブ済みのため、編集できません。</span>
					</v-tooltip>
					<v-btn
						class="mr-2"
						v-else
						color="secondary"
						dense
						dark
						@click="
							$router.push({
								name: 'daily_report_edit',
								params: { id: report.id, template_id: report.daily_report_template_id },
							})
						"
						><v-icon class="mr-2">mdi-pencil</v-icon>編集する</v-btn
					>
					<TemplateDeleteDialog :deleteMethod="deleteReport" />
				</v-row>
				<v-sheet elevation="0" class="mt-4">
					<table width="100%" class="show-dialog-table">
						<tbody>
							<tr>
								<th width="30%">件名</th>
								<td width="70%">{{ report.title }}</td>
							</tr>
							<tr>
								<th width="30%">日報作成者</th>
								<td width="70%">{{ report.user_name }}</td>
							</tr>
							<tr>
								<th width="30%">日報テンプレート名</th>
								<td width="70%">{{ templateName }}</td>
							</tr>
							<tr>
								<th width="30%">日付</th>
								<td width="70%">{{ report.report_date | dateFormatter }}</td>
							</tr>
							<tr>
								<th width="30%">開始時間</th>
								<td width="70%">{{ report.start_at | timeFormatter }}</td>
							</tr>
							<tr>
								<th width="30%">終了時間</th>
								<td width="70%">{{ report.end_at | timeFormatter }}</td>
							</tr>
							<tr>
								<th width="30%">件名</th>
								<td width="70%">{{ report.title }}</td>
							</tr>
							<tr>
								<th width="30%">場所</th>
								<td width="70%">{{ report.place }}</td>
							</tr>
							<tr>
								<th width="30%">報告内容</th>
								<td width="70%">
									<p
										v-text="report.memo"
										style="word-break: break-all; white-space: pre-wrap; margin-bottom: 0"
									/>
								</td>
							</tr>
							<template v-for="metaValue in report.daily_report_meta_values">
								<tr v-if="metaValue.daily_report_meta_key.status != 'deleted'" :key="metaValue.id">
									<th width="30%">{{ metaKeyName(metaValue) }}</th>
									<td width="70%"><p v-text="value(metaValue)" /></td>
								</tr>
							</template>
							<tr>
								<th width="30%">日報作成日</th>
								<td width="70%">{{ report.created_at | dateTimeFormatter }}</td>
							</tr>
							<tr>
								<th width="30%">最終更新日</th>
								<td width="70%">{{ report.updated_at | dateTimeFormatter }}</td>
							</tr>
							<tr>
								<th width="30%">添付</th>
								<td width="70%">
									<template v-if="report.files_with_url.length">
										<v-btn
											small
											outlined
											class="ma-1 btn-no-transform"
											color="primary"
											v-for="file in report.files_with_url"
											:key="file.id"
											:href="file.url"
											target="_blank"
										>
											<v-icon class="mr-2">mdi-download</v-icon>
											{{ file.name }}
										</v-btn>
									</template>
									<template v-else>なし</template>
								</td>
							</tr>
						</tbody>
					</table>
					<div>
						<CommentUnit
							:comment="comment"
							v-for="comment in report.daily_report_comments"
							:key="comment.id"
							@delete-comment="deleteComment($event)"
						/>
					</div>
					<v-row justify="center" no-gutters class="pa-2 mt-2" v-if="!showCommentForm">
						<v-btn color="secondary" @click="showCommentForm = true">
							コメントフォームを表示
						</v-btn>
					</v-row>
					<CommentForm
						v-else
						:report_id="report.id"
						@add-comment="report.daily_report_comments.push($event)"
					/>
				</v-sheet>
			</v-sheet>
		</v-card>
	</v-dialog>
</template>

<script>
import axios from "axios"
import moment from "moment/min/moment.min.js"
import authenticate from "../../../helper/authenticate"
import errorHandling from "../../../helper/errorHandling"
import CommentForm from "./CommentForm.vue"
import CommentUnit from "./CommentUnit.vue"
import TemplateDeleteDialog from "../../shared/TemplateDeleteDialog.vue"

export default {
	props: {
		dailyReport: {
			type: Object,
			required: true,
			default: () => {},
		},
		open: {
			type: Boolean,
			required: true,
			default: false,
		},
	},
	components: {
		CommentForm,
		CommentUnit,
		TemplateDeleteDialog,
	},
	filters: {
		dateFormatter(date) {
			if (!date) return ""
			return moment(date).format("YYYY/MM/DD")
		},
		timeFormatter(time) {
			if (!time) return ""
			return moment(time).format("HH:mm")
		},
		dateTimeFormatter(dateTime) {
			if (!dateTime) return ""
			return moment(dateTime).format("YYYY/MM/DD HH:mm")
		},
	},

	data() {
		return {
			report: {
				id: null,
				title: null,
				user_name: null,
				report_date: null,
				start_at: null,
				end_at: null,
				title: null,
				meeting_type_ja: null,
				place: null,
				matter_name: null,
				memo: null,
				created_at: null,
				updated_at: null,
				files_with_url: [],
				daily_report_meta_values: [],
			},
			showCommentForm: false,
			deleteDialog: false,
		}
	},
	methods: {
		deleteComment(id) {
			this.report.daily_report_comments = this.report.daily_report_comments.filter(
				(comment) => comment.id != id
			)
		},
		deleteReport() {
			const headers = authenticate.setHeader()
			axios
				.delete(`/api/v1/daily_reports/${this.report.id}`, { headers })
				.then((res) => {
					this.$emit("delete-report", this.report.id)
					this.$store.dispatch("setFlash", {
						type: "notice",
						text: "日報を削除しました。",
					})
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
	},
	computed: {
		templateName() {
			if (this.report.daily_report_template) {
				const name = this.report.daily_report_template.name
				const status = this.report.daily_report_template.status
				if (status == "available") {
					return name
				} else if (status == "archived") {
					return `${name}(アーカイブ済み)`
				} else if (status == "deleted") {
					return `${name}(削除済み)`
				}
			} else {
				return null
			}
		},
		metaKeyName() {
			return (metaValue) => {
				const name = metaValue.daily_report_meta_key.name
				const status = metaValue.daily_report_meta_key.status
				if (status == "available") {
					return name
				} else if (status == "archived") {
					return `${name}(アーカイブ済み)`
				} else if (status == "deleted") {
					return `${name}(削除済み)`
				}
			}
		},
		value() {
			return (metaValue) => {
				if (!metaValue.value) return null
				switch (metaValue.daily_report_meta_key.meta_type) {
					case "string":
						return metaValue.value
					case "text":
						return metaValue.value
					case "date":
						return moment(metaValue.value).format("YYYY/MM/DD")
					case "datetime":
						return moment(metaValue.value).format("YYYY/MM/DD HH:mm")
					case "time":
						return moment(metaValue.value).format("HH:mm")
					case "integer":
						return Number(metaValue.value).toLocaleString()
					case "float":
						return Number(metaValue.value).toLocaleString()
					case "boolean":
						if (metaValue.value == true) {
							return `${metaValue.daily_report_meta_key.name}である`
						} else if (metaValue.value == false) {
							return `${metaValue.daily_report_meta_key.name}ではない`
						} else {
							return null
						}
					case "association":
						return metaValue.value.name
				}
			}
		},
	},
	watch: {
		dailyReport(newValue, _) {
			this.report = newValue
		},
		deep: true,
	},
}
</script>

<style lang="scss" scoped>
table {
	.border-top {
		border-top: 4px white solid;
	}
	thead {
		th {
			padding: 4px;
			border: 1px solid white;
			background-color: rgb(235, 235, 235);
		}
		td {
			padding: 4px;
			border: 1px solid white;
			background-color: rgb(235, 235, 235);
		}
	}
	tbody {
		th {
			padding: 4px;
			background-color: whitesmoke;
		}
		td {
			padding: 4px;
			border: 1px solid whitesmoke;
			p {
				white-space: pre-wrap;
				max-width: 100%;
			}
		}
	}
	.record-wit-btn {
		position: relative;
	}
}
.file-unit {
	display: inline-block;
	white-space: normal;
	text-decoration: underline;
	&:not(:first-child) {
		margin-top: 0.25rem;
	}
}
</style>
